import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import i18n from '../i18n.ts';
import type { Theme } from '../types/app.ts';
import { rememberPath, forgetPath, type SpatialLayoutMode } from '../lib/spatial-order.ts';

export type Locale = 'zh' | 'en';

interface UIState {
  sidebarOpen: boolean;
  theme: Theme;
  locale: Locale;
  spatialLayout: SpatialLayoutMode;
  spatialOverview: boolean;
  spatialRecents: string[];
  spatialClosed: string[];
  spatialMaximized: string | null;

  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setTheme: (theme: Theme) => void;
  setLocale: (locale: Locale) => void;
  setSpatialLayout: (layout: SpatialLayoutMode, opts?: { overview?: boolean }) => void;
  setSpatialMaximized: (id: string | null) => void;
  rememberSpatialRoute: (path: string) => void;
  forgetSpatialRoute: (path: string) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      theme: 'system',
      locale: 'zh',
      spatialLayout: 'stack',
      spatialOverview: false,
      spatialRecents: [],
      spatialClosed: [],
      spatialMaximized: null,

      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setTheme: (theme) => set({ theme }),
      setSpatialLayout: (spatialLayout, opts) =>
        set((state) => ({
          spatialLayout,
          spatialOverview: spatialLayout === 'tile' && Boolean(opts?.overview),
          spatialMaximized: spatialLayout !== state.spatialLayout ? null : state.spatialMaximized,
        })),
      setSpatialMaximized: (spatialMaximized) => set({ spatialMaximized }),
      rememberSpatialRoute: (path) =>
        set((state) => ({
          spatialRecents: rememberPath(state.spatialRecents, path),
          spatialClosed: forgetPath(state.spatialClosed, path),
        })),
      forgetSpatialRoute: (path) =>
        set((state) => ({
          spatialRecents: forgetPath(state.spatialRecents, path),
          spatialClosed: rememberPath(state.spatialClosed, path),
          spatialMaximized: state.spatialMaximized === path ? null : state.spatialMaximized,
        })),
      setLocale: (locale) => {
        i18n.changeLanguage(locale);
        set({ locale });
      },
    }),
    {
      name: 'nopanel-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        theme: state.theme,
        locale: state.locale,
        spatialLayout: state.spatialLayout,
        spatialRecents: state.spatialRecents,
        spatialClosed: state.spatialClosed,
        spatialMaximized: state.spatialMaximized,
      }),
    },
  ),
);
